import { mergeWidgetSettingsWithDefaults } from '../fixtures/default-settings.js';
import type {
  LauncherPosition,
  MobileLauncherMode,
  WidgetSettings,
  WidgetSettingsResponse,
} from '../types/widget-settings.js';
import {
  DEFAULT_CONTEXT_POPUP_DELAY_SECONDS,
  DEFAULT_MOBILE_LAUNCHER_MODE,
  DEFAULT_POWERED_BY,
  DEFAULT_PROACTIVE_GREETING_MODE,
  DEFAULT_PROACTIVE_GREETING_REENGAGE_SECONDS,
  MOBILE_BREAKPOINT_PX,
  WIDGET_SIZE_DIMENSIONS,
  type ProactiveGreetingMode,
} from '../types/widget-settings.js';
import type { I18nJson, MessageSchedule } from '../types/widget-settings.js';
import { parseContextPopupDelayByPageType } from '../utils/context-popup-delay.js';
import { normalizeCssColorOr } from '../utils/css-color.js';

const LAUNCHER_POSITIONS = new Set<string>([
  'bottom_left',
  'bottom_right',
  'top_left',
  'top_right',
]);

/**
 * Canonical positions for `.teo-root--{position}` CSS.
 * Legacy cabinet values (`right` / `left`) map to bottom_* — otherwise the
 * root gets no inset and `position:fixed` sticks to the end of the document.
 */
function parseLauncherPosition(value: string | undefined): LauncherPosition {
  const normalized = value?.trim().toLowerCase();
  if (!normalized) return 'bottom_right';
  if (LAUNCHER_POSITIONS.has(normalized)) {
    return normalized as LauncherPosition;
  }
  if (normalized === 'right') return 'bottom_right';
  if (normalized === 'left') return 'bottom_left';
  return 'bottom_right';
}

function parseMobileLauncherMode(value: string | undefined): MobileLauncherMode {
  const normalized = value?.trim().toLowerCase();
  if (normalized === 'draggable' || normalized === 'drag') return 'draggable';
  if (normalized === 'edge' || normalized === 'edge_dock' || normalized === 'dock') {
    return 'edge';
  }
  if (normalized === 'fixed') return 'fixed';
  return DEFAULT_MOBILE_LAUNCHER_MODE;
}

function parseProactiveGreetingMode(
  value: string | undefined,
): ProactiveGreetingMode {
  const normalized = value?.trim().toLowerCase();
  if (normalized === 'every_page') return 'every_page';
  if (
    normalized === 'first_dialog_reengage' ||
    normalized === 'first_dialog' ||
    normalized === 'reengage'
  ) {
    return 'first_dialog_reengage';
  }
  return DEFAULT_PROACTIVE_GREETING_MODE;
}

function parseJson<T>(value: string, fallback: T): T {
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

function parseBool(value: string | undefined, defaultValue: boolean): boolean {
  if (value === undefined) return defaultValue;
  return value === 'true' || value === '1';
}

function parseIntSafe(value: string | undefined, fallback: number): number {
  const n = Number.parseInt(value ?? '', 10);
  return Number.isFinite(n) ? n : fallback;
}

function parseQuickReplyCount(value: string | undefined): 1 | 2 | 3 {
  const n = Number.parseInt(value ?? '', 10);
  if (n === 1 || n === 2 || n === 3) return n;
  return 3;
}

function parseQuickReplySource(value: string | undefined): 'ai' | 'custom' {
  return value?.trim().toLowerCase() === 'custom' ? 'custom' : 'ai';
}

/** Derive user bubble color when WS_BG_COLOR_USER_MESSAGES is not set */
function deriveUserBubbleColor(
  windowBg: string,
  textColor: string,
): string {
  // Placeholder: implementation will use color-mix in CSS or a tiny color util
  return `color-mix(in srgb, ${textColor} 12%, ${windowBg})`;
}

export function parseWidgetSettings(
  raw: Partial<WidgetSettingsResponse> | null | undefined,
): WidgetSettings {
  const source = raw ?? {};
  const merged = mergeWidgetSettingsWithDefaults(source);
  const size =
    merged.WS_WIDGET_SIZE in WIDGET_SIZE_DIMENSIONS
      ? merged.WS_WIDGET_SIZE
      : 'small';
  const dimensions = WIDGET_SIZE_DIMENSIONS[size];

  const welcomeMessage = parseJson<I18nJson>(merged.WELCOME_MESSAGE, {});
  const exitIntentMessage = parseJson<I18nJson>(merged.EXIT_INTENT_MESSAGE, {});
  const customQuickReply = parseJson<I18nJson>(
    merged.PROACTIVE_GREETING_CUSTOM_QUICK_REPLY ?? '',
    {
      uk: 'Підкажіть, будь ласка',
      en: 'Please help me',
    },
  );
  const messageSchedule = parseJson<MessageSchedule>(
    merged.MESSAGE_SCHEDULE,
    {},
  );

  const languages = merged.LANGUAGES.split(',')
    .map((l: string) => l.trim())
    .filter(Boolean);

  const windowBg = normalizeCssColorOr(merged.WS_BG_COLOR, '#f4f4f5');
  const textColor = normalizeCssColorOr(merged.WS_TEXT_COLOR, '#18181b');

  const userMessageBg = normalizeCssColorOr(
    merged.WS_BG_COLOR_USER_MESSAGES,
    deriveUserBubbleColor(windowBg, textColor),
  );

  const incomingMessageText = normalizeCssColorOr(
    merged.WS_TEXT_COLOR_MESSAGES,
    textColor,
  );
  const outgoingMessageText = normalizeCssColorOr(
    merged.WS_TEXT_COLOR_USER_MESSAGES,
    textColor,
  );

  const contextEnabled = parseBool(merged.CONTEXT_ENABLED, true);
  // Keep legacy behavior: omitted key follows CONTEXT_ENABLED (not a fixed default).
  const proactiveGreetingEnabled = parseBool(
    source.PROACTIVE_GREETING_ENABLED,
    contextEnabled,
  );
  // Omitted → visible (backward compatible with existing embeds).
  const widgetVisible = parseBool(source.WIDGET_VISIBLE, true);

  return {
    launcher: {
      sizePx: parseIntSafe(merged.BS_SIZE, 56),
      borderRadiusPx: parseIntSafe(merged.BS_BORDER_RADIUS, 50),
      backgroundColor: normalizeCssColorOr(
        merged.BS_BG_COLOR_MESSAGES,
        '#52525b',
      ),
      textColor: normalizeCssColorOr(merged.BS_TEXT_COLOR, '#ffffff'),
      position: parseLauncherPosition(merged.BS_POSITIONING),
      offsetX: parseIntSafe(merged.BS_RETREAT_FROM_EDGE, 40),
      offsetY: parseIntSafe(merged.BS_INDENT_FROM_BELOW, 40),
      mobileMode: parseMobileLauncherMode(merged.BS_MOBILE_LAUNCHER_MODE),
    },
    popup: {
      backgroundColor: normalizeCssColorOr(merged.BS_POPUP_BG_COLOR, '#FFFFFF'),
      textColor: normalizeCssColorOr(merged.BS_POPUP_TEXT_COLOR, '#18181b'),
      buttonBackgroundColor: normalizeCssColorOr(
        merged.BS_POPUP_BUTTON_BG_COLOR,
        '#52525b',
      ),
      buttonTextColor: normalizeCssColorOr(
        merged.BS_POPUP_BUTTON_TEXT_COLOR,
        '#ffffff',
      ),
    },
    window: {
      size,
      borderRadiusPx: parseIntSafe(merged.WS_BORDER_RADIUS, 16),
      backgroundColor: windowBg,
      headerBackgroundColor: normalizeCssColorOr(
        merged.WS_BG_COLOR_HEADER,
        '#3f3f46',
      ),
      headerTextColor: normalizeCssColorOr(
        merged.WS_TEXT_COLOR_HEADER,
        '#fafafa',
      ),
      incomingMessageBackgroundColor: normalizeCssColorOr(
        merged.WS_BG_COLOR_MESSAGES,
        '#ffffff',
      ),
      incomingMessageTextColor: incomingMessageText,
      outgoingMessageBackgroundColor: userMessageBg,
      outgoingMessageTextColor: outgoingMessageText,
      textColor,
      fontFamily: merged.WS_FONT_FAMILY,
      widthPx: dimensions.width,
      heightPx: dimensions.height,
      mobileBreakpointPx: MOBILE_BREAKPOINT_PX,
    },
    content: {
      shopName: merged.SHOP_NAME,
      widgetUrl: merged.URL,
      welcomeMessage,
      exitIntentMessage,
      languages,
      messageSchedule,
      widgetVisible,
      // Default on — page parser / behavior; stage often omits the key.
      contextEnabled,
      proactiveGreetingEnabled,
      aiAgentName: merged.AI_AGENT_NAME?.trim() || 'Teo',
      aiAvatarUrl: merged.AI_AVATAR_URL?.trim() || null,
      contextPopupDelaySeconds: parseIntSafe(
        merged.CONTEXT_POPUP_DELAY_SECONDS,
        DEFAULT_CONTEXT_POPUP_DELAY_SECONDS,
      ),
      contextPopupDelayByPageType: parseContextPopupDelayByPageType(
        merged.CONTEXT_POPUP_DELAY_BY_PAGE_TYPE,
      ),
      proactiveGreetingAutoOpenChat: parseBool(
        merged.PROACTIVE_GREETING_AUTO_OPEN_CHAT,
        false,
      ),
      proactiveGreetingQuickReplies: parseBool(
        merged.PROACTIVE_GREETING_QUICK_REPLIES,
        false,
      ),
      proactiveGreetingQuickReplyCount: parseQuickReplyCount(
        merged.PROACTIVE_GREETING_QUICK_REPLY_COUNT,
      ),
      proactiveGreetingQuickReplySource: parseQuickReplySource(
        merged.PROACTIVE_GREETING_QUICK_REPLY_SOURCE,
      ),
      proactiveGreetingCustomQuickReply: customQuickReply,
      proactiveGreetingMode: parseProactiveGreetingMode(
        merged.PROACTIVE_GREETING_MODE,
      ),
      proactiveGreetingReengageSeconds: Math.max(
        60,
        parseIntSafe(
          merged.PROACTIVE_GREETING_REENGAGE_SECONDS,
          DEFAULT_PROACTIVE_GREETING_REENGAGE_SECONDS,
        ),
      ),
      poweredBy: {
        enabled: parseBool(merged.POWERED_BY_ENABLED, false),
        text: merged.POWERED_BY_TEXT?.trim() || DEFAULT_POWERED_BY.text,
        url: merged.POWERED_BY_URL?.trim() || DEFAULT_POWERED_BY.url,
      },
    },
  };
}

/** CSS custom properties injected into Shadow DOM host */
export function settingsToCssVars(
  settings: WidgetSettings,
): Record<string, string> {
  const { launcher, popup, window: win } = settings;

  return {
    '--teo-launcher-size': `${launcher.sizePx}px`,
    '--teo-launcher-radius': `${launcher.borderRadiusPx}px`,
    '--teo-launcher-bg': launcher.backgroundColor,
    '--teo-launcher-color': launcher.textColor,
    '--teo-launcher-offset-x': `${launcher.offsetX}px`,
    '--teo-launcher-offset-y': `${launcher.offsetY}px`,

    '--teo-popup-bg': popup.backgroundColor,
    '--teo-popup-text': popup.textColor,
    '--teo-popup-btn-bg': popup.buttonBackgroundColor,
    '--teo-popup-btn-text': popup.buttonTextColor,

    '--teo-window-width': `${win.widthPx}px`,
    '--teo-window-height': `${win.heightPx}px`,
    '--teo-window-radius': `${win.borderRadiusPx}px`,
    '--teo-window-bg': win.backgroundColor,
    '--teo-header-bg': win.headerBackgroundColor,
    '--teo-header-text': win.headerTextColor,
    '--teo-bubble-incoming-bg': win.incomingMessageBackgroundColor,
    '--teo-bubble-incoming-text': win.incomingMessageTextColor,
    '--teo-bubble-outgoing-bg': win.outgoingMessageBackgroundColor,
    '--teo-bubble-outgoing-text': win.outgoingMessageTextColor,
    '--teo-bubble-bot-bg': win.incomingMessageBackgroundColor,
    '--teo-bubble-bot-text': win.incomingMessageTextColor,
    '--teo-bubble-user-bg': win.outgoingMessageBackgroundColor,
    '--teo-bubble-user-text': win.outgoingMessageTextColor,
    '--teo-text': win.textColor,
    '--teo-font-family': win.fontFamily,

    '--teo-mobile-breakpoint': `${win.mobileBreakpointPx}px`,
  };
}

export function getLocalizedText(
  map: I18nJson,
  locale: string,
  fallbackLocale = 'en',
): string {
  return map[locale] ?? map[fallbackLocale] ?? Object.values(map)[0] ?? '';
}
